import { api } from "../../api/client.js";

export type CitationStyle = "gb-t-7714-2015-numeric" | "apa-7" | "mla-9" | "chicago-author-date" | "vancouver";

export type WorkDetail = {
  id: string;
  workType: string;
  title: string;
  subtitle?: string | null;
  language?: string | null;
  publicationStage: string;
  recordStatus: string;
  issued?: { year: number; month?: number | null; day?: number | null } | null;
  authors: Array<string | {
    name?: string;
    givenName?: string | null;
    familyName?: string | null;
    orcid?: string | null;
    affiliations?: Array<{ name: string; ror?: string | null }>;
  }>;
  keywords: string[];
  identifiers: Array<{ scheme: string; value_normalized: string; canonical_url?: string | null }>;
  publication: {
    venueId?: string | null;
    venueTitle?: string | null;
    volume?: string | null;
    issue?: string | null;
    pages?: string | null;
    articleNumber?: string | null;
    publisher?: string | null;
  };
  venue?: {
    id: string;
    title: string;
    shortTitle?: string | null;
    homepageUrl?: string | null;
    publisher?: string | null;
    mediaAssets: Array<{ media_role?: string; image_url?: string; asset_ref?: string; alt_text?: string }>;
  } | null;
  abstracts: Array<{ id: string; kind: string; language?: string | null; content: string; sections?: Array<{ label?: string; content: string }> }>;
  links: Array<{ type: string; url: string; accessStatus: string }>;
  metrics: Array<{ targetType: "work" | "venue"; type: string; value: string; period?: string | null; provider: string; verifiedAt?: string | null }>;
  isSaved: boolean;
  contentState: "no_content" | "abstract_only" | "processing" | "partial_full_text" | "full_text" | "parse_failed";
  contentAssets: Array<{ id: string; media_type: string; completeness: string; section_count: number; element_count: number; fileUrl?: string | null }>;
  sessionContext?: { id: string; projectId: string | null } | null;
};

export type WorkRelation = {
  edgeId: string;
  position?: number | null;
  rawReferenceText?: string | null;
  resolutionStatus: string;
  work?: { id: string; title: string; authors: Array<string | Record<string, any>>; year?: number | null; venue?: string | null } | null;
};

export type WorkRelationPage = {
  items: WorkRelation[];
  nextCursor: string | null;
  total: number;
  resolvedCount: number;
  unresolvedCount: number;
};

export type WorkFavoriteEvent = { workId: string; isSaved: boolean; sessionId?: string | null };

const workFavoriteChannelName = "truth-seeker-work-favorites";

function publishWorkFavorite(event: WorkFavoriteEvent) {
  if (typeof BroadcastChannel === "undefined") return;
  const channel = new BroadcastChannel(workFavoriteChannelName);
  channel.postMessage(event);
  channel.close();
}

export function subscribeWorkFavorites(listener: (event: WorkFavoriteEvent) => void) {
  if (typeof BroadcastChannel === "undefined") return () => undefined;
  const channel = new BroadcastChannel(workFavoriteChannelName);
  channel.onmessage = (message) => {
    const event = message.data as Partial<WorkFavoriteEvent>;
    if (typeof event.workId === "string" && typeof event.isSaved === "boolean") listener(event as WorkFavoriteEvent);
  };
  return () => channel.close();
}

export function workDetailUrl(workId: string, sessionId?: string | null) {
  const params = new URLSearchParams();
  if (sessionId) params.set("sessionId", sessionId);
  return `/papers/${encodeURIComponent(workId)}${params.size ? `?${params.toString()}` : ""}`;
}

export async function getWork(workId: string, sessionId?: string | null) {
  const params = new URLSearchParams();
  if (sessionId) params.set("sessionId", sessionId);
  const data = await api<{ work: WorkDetail }>(`/api/works/${encodeURIComponent(workId)}${params.size ? `?${params.toString()}` : ""}`);
  return data.work;
}

export async function getWorkContent(workId: string) {
  return api<{
    assetId?: string;
    sections: Array<{ id: string; parent_section_id?: string | null; position: number; section_type: string; heading?: string | null; content: string; page_start?: number | null; page_end?: number | null }>;
    elements: Array<{ id: string; section_id?: string | null; element_type: "figure" | "table"; position: number; label?: string | null; caption?: string | null; page_number?: number | null; content_asset_id?: string | null }>;
  }>(`/api/works/${encodeURIComponent(workId)}/content`);
}

export async function getWorkRelations(workId: string, direction: "references" | "cited_by", cursor?: string | null) {
  const params = new URLSearchParams({ direction, limit: "30" });
  if (cursor) params.set("cursor", cursor);
  return api<WorkRelationPage>(`/api/works/${encodeURIComponent(workId)}/relations?${params.toString()}`);
}

export async function setWorkFavorite(workId: string, saved: boolean, sessionId?: string | null) {
  const result = await api<{ workId: string; isSaved: boolean; savedAt?: string; sessionId?: string | null }>(`/api/works/${encodeURIComponent(workId)}/favorite`, {
    method: saved ? "PUT" : "DELETE",
    ...(saved ? { body: JSON.stringify({ ...(sessionId ? { sessionId } : {}) }) } : {})
  });
  publishWorkFavorite({ workId: result.workId, isSaved: result.isSaved, sessionId: result.sessionId });
  return result;
}

export async function getWorkCitation(workId: string, style: CitationStyle, accessed?: string) {
  const params = new URLSearchParams({ style });
  if (accessed) params.set("accessed", accessed);
  const data = await api<{ citation: { style: CitationStyle; text: string; bibtex: string; ris: string; missingFields: string[]; accessDateRecommended?: boolean } }>(
    `/api/works/${encodeURIComponent(workId)}/citation?${params.toString()}`
  );
  return data.citation;
}
